import { Component, EventEmitter, Input, Output } from '@angular/core';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { AuthorizenetCheckoutService } from '../authorizenet-checkout.service';
import {
    AuthorizeNetCustomerProfileBillingFields,
    AuthorizeNetCustomerProfileResponse,
    AuthorizeNetCustomerProfileSummary,
    AuthorizeNetCustomerProfileSubscriptionsExport,
    AuthorizeNetCustomerProfileSubscriptionExportDetail,
    AuthorizeNetSettledBatchListParams,
    AuthorizeNetSettledBatchListResponse,
    AuthorizeNetSubscriptionDetailsListItem,
    AuthorizeNetSubscriptionDetail,
    AuthorizeNetSubscriptionListParams,
    AuthorizeNetSubscriptionListResponse,
    AuthorizeNetSubscriptionOrderBy,
    AuthorizeNetSubscriptionResponse,
    AuthorizeNetSubscriptionSearchType,
    AuthorizeNetTransactionDetailsResponse,
    AuthorizeNetTransactionListOrderBy,
    AuthorizeNetTransactionListParams,
    AuthorizeNetTransactionListResponse,
    AuthorizeNetUnsettledTransactionListParams,
    AuthorizeNetUnsettledTransactionListResponse
} from '../IAuthorizeNetSubscriptions';

@Component({
    selector: 'ef-authorizenet-subscriptions-dialog',
    templateUrl: './authorizenet-subscriptions-dialog.component.html',
    styleUrls: ['./authorizenet-subscriptions-dialog.component.scss'],
    standalone: false
})
export class AuthorizenetSubscriptionsDialogComponent {
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    subscriptionsLoading = false;
    subscriptionsError: string | null = null;
    subscriptionsResponse: AuthorizeNetSubscriptionListResponse | null = null;

    subscriptionDetailId = '';
    subscriptionDetailIncludeTransactions = true;
    subscriptionDetailLoading = false;
    subscriptionDetailError: string | null = null;
    subscriptionDetailResponse: AuthorizeNetSubscriptionResponse | null = null;

    subscriptionDetailsListIncludeTransactions = false;
    subscriptionDetailsListLoading = false;
    subscriptionDetailsListError: string | null = null;
    subscriptionDetailsListResponse: AuthorizeNetSubscriptionDetailsListItem[] | null = null;

    customerProfileId = '';
    customerProfileUnmaskExpirationDate = false;
    customerProfileIncludeIssuerInfo = false;
    customerProfileLoading = false;
    customerProfileError: string | null = null;
    customerProfileResponse: AuthorizeNetCustomerProfileResponse | null = null;

    profileSubscriptionsExportIncludeTransactions = false;
    profileSubscriptionsExportIncludeInactive = false;
    profileSubscriptionsExportLoading = false;
    profileSubscriptionsExportError: string | null = null;
    profileSubscriptionsExportResponse: AuthorizeNetCustomerProfileSubscriptionsExport[] | null =
        null;
    profileSubscriptionsExportProfileCount = 0;
    profileSubscriptionsExportSubscriptionCount = 0;

    customerProfileSummariesLoading = false;
    customerProfileSummariesError: string | null = null;
    customerProfileSummariesResponse: AuthorizeNetCustomerProfileSummary[] | null = null;
    customerProfileSummariesAllVerified: boolean | null = null;
    customerProfileSummariesVerificationMessage: string | null = null;

    transactionListLoading = false;
    transactionListError: string | null = null;
    transactionListResponse: AuthorizeNetTransactionListResponse | null = null;

    transactionDetailsTransId = '';
    transactionDetailsLoading = false;
    transactionDetailsError: string | null = null;
    transactionDetailsResponse: AuthorizeNetTransactionDetailsResponse | null = null;

    settledBatchListLoading = false;
    settledBatchListError: string | null = null;
    settledBatchListResponse: AuthorizeNetSettledBatchListResponse | null = null;

    unsettledTransactionListLoading = false;
    unsettledTransactionListError: string | null = null;
    unsettledTransactionListResponse: AuthorizeNetUnsettledTransactionListResponse | null = null;

    subscriptionParams: AuthorizeNetSubscriptionListParams = {
        searchType: 'subscriptionActive',
        orderBy: 'id',
        orderDescending: false,
        limit: 100,
        offset: 1
    };

    transactionListParams: AuthorizeNetTransactionListParams = {
        batchId: '',
        orderBy: 'submitTimeUTC',
        orderDescending: true,
        limit: 100,
        offset: 1
    };

    settledBatchListParams: AuthorizeNetSettledBatchListParams = {
        includeStatistics: true,
        firstSettlementDate: '',
        lastSettlementDate: ''
    };

    unsettledTransactionListParams: AuthorizeNetUnsettledTransactionListParams = {
        orderBy: 'submitTimeUTC',
        orderDescending: true,
        limit: 100,
        offset: 1
    };

    searchTypeOptions: Array<{ label: string; value: AuthorizeNetSubscriptionSearchType }> = [
        { label: 'Active subscriptions', value: 'subscriptionActive' },
        { label: 'Inactive subscriptions', value: 'subscriptionInactive' },
        { label: 'Expiring this month', value: 'subscriptionExpiringThisMonth' },
        { label: 'Card expiring this month', value: 'cardExpiringThisMonth' }
    ];

    orderByOptions: Array<{ label: string; value: AuthorizeNetSubscriptionOrderBy }> = [
        { label: 'Subscription ID', value: 'id' },
        { label: 'Name', value: 'name' },
        { label: 'Status', value: 'status' },
        { label: 'Created (UTC)', value: 'createTimeStampUTC' },
        { label: 'Last name', value: 'lastName' },
        { label: 'First name', value: 'firstName' },
        { label: 'Account number', value: 'accountNumber' },
        { label: 'Amount', value: 'amount' },
        { label: 'Past occurrences', value: 'pastOccurrences' }
    ];

    transactionListOrderByOptions: Array<{
        label: string;
        value: AuthorizeNetTransactionListOrderBy;
    }> = [
        { label: 'Transaction ID', value: 'id' },
        { label: 'Submitted (UTC)', value: 'submitTimeUTC' }
    ];

    constructor(private authorizeNetCheckoutService: AuthorizenetCheckoutService) {}

    onDialogHide(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    loadSubscriptions(): void {
        this.subscriptionsLoading = true;
        this.subscriptionsError = null;
        this.subscriptionsResponse = null;

        this.authorizeNetCheckoutService.getSubscriptions(this.subscriptionParams).subscribe({
            next: res => {
                this.subscriptionsResponse = res;
                this.subscriptionsLoading = false;
            },
            error: err => {
                this.subscriptionsLoading = false;
                this.subscriptionsError = this.getAuthorizeNetErrorMessage(err);
                this.subscriptionsResponse = err?.error ?? null;
            }
        });
    }

    useSubscriptionIdFromList(id: number | string | undefined): void {
        if (id != null && id !== '') {
            this.subscriptionDetailId = String(id);
        }
    }

    useCustomerProfileIdFromList(profileId: number | string | undefined): void {
        if (profileId != null && profileId !== '') {
            this.customerProfileId = String(profileId);
        }
    }

    useCustomerProfileIdFromSubscriptionDetail(): void {
        const sub = this.subscriptionDetailResponse?.subscription as Record<string, unknown> | undefined;
        if (!sub) {
            return;
        }
        const profile = sub['profile'] as Record<string, unknown> | undefined;
        const id =
            profile?.['customerProfileId'] ??
            (sub['profile'] as { customerProfileId?: string | number })?.customerProfileId;
        if (id != null && id !== '') {
            this.customerProfileId = String(id);
        }
    }

    loadSubscriptionById(): void {
        const id = this.subscriptionDetailId?.trim();
        if (!id) {
            this.subscriptionDetailError = 'Enter a subscription ID.';
            return;
        }

        this.subscriptionDetailLoading = true;
        this.subscriptionDetailError = null;
        this.subscriptionDetailResponse = null;

        this.authorizeNetCheckoutService
            .getSubscription(id, this.subscriptionDetailIncludeTransactions)
            .subscribe({
                next: res => {
                    this.subscriptionDetailResponse = res;
                    this.subscriptionDetailLoading = false;
                    this.useCustomerProfileIdFromSubscriptionDetail();
                },
                error: err => {
                    this.subscriptionDetailLoading = false;
                    this.subscriptionDetailError = this.getAuthorizeNetErrorMessage(err);
                    this.subscriptionDetailResponse = err?.error ?? null;
                }
            });
    }

    loadSubscriptionDetailsFromList(): void {
        const subscriptionIds = (this.subscriptionsResponse?.subscriptionDetails ?? [])
            .map(subscription => subscription.id)
            .filter(subscriptionId => subscriptionId != null)
            .map(subscriptionId => String(subscriptionId));

        if (subscriptionIds.length === 0) {
            this.subscriptionDetailsListError =
                'Load subscriptions first; no subscription IDs are available on this page.';
            return;
        }

        this.subscriptionDetailsListLoading = true;
        this.subscriptionDetailsListError = null;
        this.subscriptionDetailsListResponse = null;

        from(subscriptionIds)
            .pipe(
                mergeMap(subscriptionId =>
                    this.authorizeNetCheckoutService
                        .getSubscription(
                            subscriptionId,
                            this.subscriptionDetailsListIncludeTransactions
                        )
                        .pipe(
                            map(
                                response =>
                                    ({
                                        subscriptionId,
                                        ...response
                                    }) as AuthorizeNetSubscriptionDetailsListItem
                            ),
                            catchError(err =>
                                of({
                                    subscriptionId,
                                    error: this.getAuthorizeNetErrorMessage(err)
                                } as AuthorizeNetSubscriptionDetailsListItem)
                            )
                        ),
                    5
                ),
                toArray()
            )
            .subscribe({
                next: details => {
                    this.subscriptionDetailsListResponse = details;
                    this.subscriptionDetailsListLoading = false;
                },
                error: err => {
                    this.subscriptionDetailsListLoading = false;
                    this.subscriptionDetailsListError = this.getAuthorizeNetErrorMessage(err);
                }
            });
    }

    loadCustomerProfile(): void {
        const id = this.customerProfileId?.trim();
        if (!id) {
            this.customerProfileError = 'Enter a customer profile ID.';
            return;
        }

        this.customerProfileLoading = true;
        this.customerProfileError = null;
        this.customerProfileResponse = null;

        this.authorizeNetCheckoutService
            .getCustomerProfile({
                customerProfileId: id,
                unmaskExpirationDate: this.customerProfileUnmaskExpirationDate,
                includeIssuerInfo: this.customerProfileIncludeIssuerInfo
            })
            .subscribe({
                next: res => {
                    this.customerProfileResponse = res;
                    this.customerProfileLoading = false;
                },
                error: err => {
                    this.customerProfileLoading = false;
                    this.customerProfileError = this.getAuthorizeNetErrorMessage(err);
                    this.customerProfileResponse = err?.error ?? null;
                }
            });
    }

    /**
     * Builds JSON for every customer profile found on active + inactive subscriptions:
     * [{ customerProfileId, subscriptionIds, subscriptions: [detail per id] }, ...].
     */
    buildCustomerProfileSubscriptionsJson(): void {
        this.profileSubscriptionsExportLoading = true;
        this.profileSubscriptionsExportError = null;
        this.profileSubscriptionsExportResponse = null;
        this.profileSubscriptionsExportProfileCount = 0;
        this.profileSubscriptionsExportSubscriptionCount = 0;

        this.collectAllCustomerProfileSubscriptionIds()
            .pipe(
                switchMap(profileToSubscriptionIds => {
                    const customerProfileIds = Object.keys(profileToSubscriptionIds).sort(
                        (left, right) => left.localeCompare(right)
                    );

                    if (customerProfileIds.length === 0) {
                        return of([] as AuthorizeNetCustomerProfileSubscriptionsExport[]);
                    }

                    return from(customerProfileIds).pipe(
                        mergeMap(customerProfileId => {
                            const subscriptionIds = profileToSubscriptionIds[customerProfileId] ?? [];
                            if (subscriptionIds.length === 0) {
                                return of({
                                    customerProfileId,
                                    subscriptionIds: [],
                                    subscriptions: []
                                } as AuthorizeNetCustomerProfileSubscriptionsExport);
                            }

                            return from(subscriptionIds).pipe(
                                mergeMap(
                                    subscriptionId =>
                                        this.authorizeNetCheckoutService
                                            .getSubscription(
                                                subscriptionId,
                                                this
                                                    .profileSubscriptionsExportIncludeTransactions
                                            )
                                            .pipe(
                                                map(response =>
                                                    this.mapSubscriptionExportDetail(
                                                        subscriptionId,
                                                        response
                                                    )
                                                ),
                                                catchError(err =>
                                                    of({
                                                        subscriptionId,
                                                        error: this.getAuthorizeNetErrorMessage(
                                                            err
                                                        )
                                                    } as AuthorizeNetCustomerProfileSubscriptionExportDetail)
                                                )
                                            ),
                                    5
                                ),
                                toArray(),
                                map(
                                    subscriptions =>
                                        ({
                                            customerProfileId,
                                            subscriptionIds,
                                            subscriptions
                                        }) as AuthorizeNetCustomerProfileSubscriptionsExport
                                )
                            );
                        }, 3),
                        toArray()
                    );
                })
            )
            .subscribe({
                next: exportPayload => {
                    const sorted = exportPayload.sort((left, right) =>
                        left.customerProfileId.localeCompare(right.customerProfileId)
                    );
                    this.profileSubscriptionsExportResponse = sorted;
                    this.profileSubscriptionsExportProfileCount = sorted.length;
                    this.profileSubscriptionsExportSubscriptionCount = sorted.reduce(
                        (total, profile) => total + (profile.subscriptionIds?.length ?? 0),
                        0
                    );
                    this.profileSubscriptionsExportLoading = false;
                },
                error: err => {
                    this.profileSubscriptionsExportLoading = false;
                    this.profileSubscriptionsExportError =
                        this.getAuthorizeNetErrorMessage(err);
                }
            });
    }

    private collectAllCustomerProfileSubscriptionIds() {
        const searchTypes: AuthorizeNetSubscriptionSearchType[] =
            this.profileSubscriptionsExportIncludeInactive
                ? ['subscriptionActive', 'subscriptionInactive']
                : ['subscriptionActive'];
        const pageSize = 1000;

        return from(searchTypes).pipe(
            mergeMap(searchType =>
                this.collectSubscriptionRowsForSearchType(searchType, pageSize)
            ),
            toArray(),
            map((pages: Array<Array<{ customerProfileId: string; subscriptionId: string }>>) => {
                const profileToSubscriptionIds: Record<string, string[]> = {};

                for (const rows of pages) {
                    for (const row of rows) {
                        const customerProfileId = row.customerProfileId;
                        const subscriptionId = row.subscriptionId;
                        if (!customerProfileId || !subscriptionId) {
                            continue;
                        }

                        const existing = profileToSubscriptionIds[customerProfileId] ?? [];
                        if (!existing.includes(subscriptionId)) {
                            existing.push(subscriptionId);
                            profileToSubscriptionIds[customerProfileId] = existing;
                        }
                    }
                }

                return profileToSubscriptionIds;
            })
        );
    }

    private collectSubscriptionRowsForSearchType(
        searchType: AuthorizeNetSubscriptionSearchType,
        pageSize: number
    ) {
        type SubscriptionRow = { customerProfileId: string; subscriptionId: string };

        const collectPage = (offset: number, accumulated: SubscriptionRow[]) =>
            this.authorizeNetCheckoutService
                .getSubscriptions({
                    searchType,
                    orderBy: 'id',
                    orderDescending: false,
                    limit: pageSize,
                    offset
                })
                .pipe(
                    switchMap(response => {
                        const pageRows: SubscriptionRow[] = (response.subscriptionDetails ?? [])
                            .map(subscription => ({
                                customerProfileId: String(
                                    subscription.customerProfileId ?? ''
                                ).trim(),
                                subscriptionId: String(subscription.id ?? '').trim()
                            }))
                            .filter(
                                row =>
                                    row.customerProfileId !== '' &&
                                    row.subscriptionId !== '' &&
                                    row.subscriptionId !== 'undefined' &&
                                    row.subscriptionId !== 'null'
                            );

                        const nextAccumulated = accumulated.concat(pageRows);
                        const total = response.totalNumInResultSet ?? 0;
                        const pageCount = response.subscriptionDetails?.length ?? 0;
                        const retrievedSoFar = (offset - 1) * pageSize + pageCount;

                        if (pageCount === 0 || pageCount < pageSize || retrievedSoFar >= total) {
                            return of(nextAccumulated);
                        }

                        return collectPage(offset + 1, nextAccumulated);
                    })
                );

        return collectPage(1, [] as SubscriptionRow[]);
    }

    private mapSubscriptionExportDetail(
        subscriptionId: string,
        response: AuthorizeNetSubscriptionResponse
    ): AuthorizeNetCustomerProfileSubscriptionExportDetail {
        const root = this.asRecord(response);
        const subscription =
            this.asRecord(this.readRecordValue(root, 'subscription')) ?? root;
        const paymentSchedule = this.asRecord(
            this.readRecordValue(subscription, 'paymentSchedule')
        );
        const amountValue = this.readRecordValue(subscription, 'amount');
        const startDateValue =
            this.readRecordValue(paymentSchedule, 'startDate') ??
            this.readRecordValue(subscription, 'startDate');

        return {
            subscriptionId,
            amount:
                amountValue == null || amountValue === ''
                    ? null
                    : (amountValue as number | string),
            startDate: this.formatExportDate(startDateValue)
        };
    }

    private formatExportDate(value: unknown): string | null {
        if (value == null || value === '') {
            return null;
        }

        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return value.toISOString().slice(0, 10);
        }

        const text = String(value).trim();
        if (!text) {
            return null;
        }

        const parsed = new Date(text);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed.toISOString().slice(0, 10);
        }

        return text;
    }

    loadCustomerProfileSummariesFromSubscriptions(): void {
        const subscriptions = this.subscriptionsResponse?.subscriptionDetails ?? [];
        const subscriptionIds = subscriptions
            .map(subscription => subscription.id)
            .filter(subscriptionId => subscriptionId != null)
            .map(subscriptionId => String(subscriptionId));

        if (subscriptionIds.length === 0) {
            this.customerProfileSummariesError =
                'Load subscriptions first; no subscription IDs are available on this page.';
            return;
        }

        this.customerProfileSummariesLoading = true;
        this.customerProfileSummariesError = null;
        this.customerProfileSummariesResponse = null;
        this.customerProfileSummariesAllVerified = null;
        this.customerProfileSummariesVerificationMessage = null;

        from(subscriptionIds)
            .pipe(
                mergeMap(subscriptionId => {
                    const listItem = subscriptions.find(
                        subscription => String(subscription.id) === subscriptionId
                    );
                    const customerProfileId =
                        listItem?.customerProfileId != null
                            ? String(listItem.customerProfileId)
                            : undefined;

                    return this.authorizeNetCheckoutService.getSubscription(subscriptionId, false).pipe(
                        map(response => ({
                            subscriptionId,
                            customerProfileId,
                            billTo: this.extractSubscriptionBillingFields(response, listItem),
                            error: undefined as string | undefined
                        })),
                        catchError(err =>
                            of({
                                subscriptionId,
                                customerProfileId,
                                billTo: this.extractSubscriptionBillingFields(
                                    { subscription: {} },
                                    listItem
                                ),
                                error: this.getAuthorizeNetErrorMessage(err)
                            })
                        )
                    );
                }, 5),
                toArray(),
                switchMap(subscriptionSnapshots => {
                    const profileIds = Array.from(
                        new Set(
                            subscriptionSnapshots
                                .map(snapshot => snapshot.customerProfileId)
                                .filter(
                                    (profileId): profileId is string =>
                                        profileId != null && profileId !== ''
                                )
                        )
                    );

                    if (profileIds.length === 0) {
                        throw new Error(
                            'No customer profile IDs are available on the loaded subscriptions.'
                        );
                    }

                    return from(profileIds).pipe(
                        mergeMap(profileId => {
                            const relatedSubscriptions = subscriptionSnapshots.filter(
                                snapshot => snapshot.customerProfileId === profileId
                            );

                            return this.authorizeNetCheckoutService
                                .getCustomerProfile({ customerProfileId: profileId })
                                .pipe(
                                    map(response =>
                                        this.buildVerifiedCustomerProfileSummary(
                                            profileId,
                                            response,
                                            relatedSubscriptions
                                        )
                                    ),
                                    catchError(err =>
                                        of({
                                            customerProfileId: profileId,
                                            subscriptionIds: relatedSubscriptions.map(
                                                snapshot => snapshot.subscriptionId
                                            ),
                                            verified: false,
                                            verificationMismatches: [
                                                this.getAuthorizeNetErrorMessage(err)
                                            ],
                                            error: this.getAuthorizeNetErrorMessage(err)
                                        } as AuthorizeNetCustomerProfileSummary)
                                    )
                                );
                        }, 5),
                        toArray()
                    );
                })
            )
            .subscribe({
                next: summaries => {
                    const sortedSummaries = this.sortCustomerProfileSummariesByCompany(summaries);
                    this.customerProfileSummariesResponse = sortedSummaries;
                    const verifiedCount = sortedSummaries.filter(summary => summary.verified).length;
                    this.customerProfileSummariesAllVerified =
                        sortedSummaries.length > 0 && verifiedCount === sortedSummaries.length;
                    this.customerProfileSummariesVerificationMessage =
                        this.customerProfileSummariesAllVerified
                            ? `All ${sortedSummaries.length} customer profile(s) are 100% verified against subscription billing fields.`
                            : `${verifiedCount} of ${sortedSummaries.length} customer profile(s) verified against subscription billing fields.`;
                    this.customerProfileSummariesLoading = false;
                },
                error: err => {
                    this.customerProfileSummariesLoading = false;
                    this.customerProfileSummariesError = this.getAuthorizeNetErrorMessage(err);
                }
            });
    }

    loadTransactionList(): void {
        const batchId = this.transactionListParams.batchId?.trim();
        if (!batchId) {
            this.transactionListError = 'Enter a batch ID.';
            return;
        }

        this.transactionListLoading = true;
        this.transactionListError = null;
        this.transactionListResponse = null;

        this.authorizeNetCheckoutService
            .getTransactionList({
                ...this.transactionListParams,
                batchId
            })
            .subscribe({
                next: res => {
                    this.transactionListResponse = res;
                    this.transactionListLoading = false;
                },
                error: err => {
                    this.transactionListLoading = false;
                    this.transactionListError = this.getAuthorizeNetErrorMessage(err);
                    this.transactionListResponse = err?.error ?? null;
                }
            });
    }

    useTransactionIdFromList(transId: number | string | undefined): void {
        if (transId != null && transId !== '') {
            this.transactionDetailsTransId = String(transId);
        }
    }

    loadTransactionDetails(): void {
        const transId = this.transactionDetailsTransId?.trim();
        if (!transId) {
            this.transactionDetailsError = 'Enter a transaction ID.';
            return;
        }

        this.transactionDetailsLoading = true;
        this.transactionDetailsError = null;
        this.transactionDetailsResponse = null;

        this.authorizeNetCheckoutService.getTransactionDetails(transId).subscribe({
            next: res => {
                this.transactionDetailsResponse = res;
                this.transactionDetailsLoading = false;
            },
            error: err => {
                this.transactionDetailsLoading = false;
                this.transactionDetailsError = this.getAuthorizeNetErrorMessage(err);
                this.transactionDetailsResponse = err?.error ?? null;
            }
        });
    }

    useBatchIdFromList(batchId: number | string | undefined): void {
        if (batchId != null && batchId !== '') {
            this.transactionListParams.batchId = String(batchId);
        }
    }

    loadUnsettledTransactionList(): void {
        this.unsettledTransactionListLoading = true;
        this.unsettledTransactionListError = null;
        this.unsettledTransactionListResponse = null;

        this.authorizeNetCheckoutService
            .getUnsettledTransactionList(this.unsettledTransactionListParams)
            .subscribe({
                next: res => {
                    this.unsettledTransactionListResponse = res;
                    this.unsettledTransactionListLoading = false;
                },
                error: err => {
                    this.unsettledTransactionListLoading = false;
                    this.unsettledTransactionListError = this.getAuthorizeNetErrorMessage(err);
                    this.unsettledTransactionListResponse = err?.error ?? null;
                }
            });
    }

    loadSettledBatchList(): void {
        this.settledBatchListLoading = true;
        this.settledBatchListError = null;
        this.settledBatchListResponse = null;

        this.authorizeNetCheckoutService
            .getSettledBatchList({
                includeStatistics: this.settledBatchListParams.includeStatistics,
                firstSettlementDate: this.settledBatchListParams.firstSettlementDate?.trim() || undefined,
                lastSettlementDate: this.settledBatchListParams.lastSettlementDate?.trim() || undefined
            })
            .subscribe({
                next: res => {
                    this.settledBatchListResponse = res;
                    this.settledBatchListLoading = false;
                },
                error: err => {
                    this.settledBatchListLoading = false;
                    this.settledBatchListError = this.getAuthorizeNetErrorMessage(err);
                    this.settledBatchListResponse = err?.error ?? null;
                }
            });
    }

    private sortCustomerProfileSummariesByCompany(
        summaries: AuthorizeNetCustomerProfileSummary[]
    ): AuthorizeNetCustomerProfileSummary[] {
        return [...summaries].sort((left, right) => {
            const leftCompany = (left.company ?? '').trim().toLocaleLowerCase();
            const rightCompany = (right.company ?? '').trim().toLocaleLowerCase();

            if (!leftCompany && rightCompany) {
                return 1;
            }
            if (leftCompany && !rightCompany) {
                return -1;
            }

            return leftCompany.localeCompare(rightCompany);
        });
    }

    private buildVerifiedCustomerProfileSummary(
        customerProfileId: string,
        profileResponse: AuthorizeNetCustomerProfileResponse,
        relatedSubscriptions: Array<{
            subscriptionId: string;
            customerProfileId?: string;
            billTo?: AuthorizeNetCustomerProfileBillingFields;
            error?: string;
        }>
    ): AuthorizeNetCustomerProfileSummary {
        const summary = this.mapCustomerProfileSummary(customerProfileId, profileResponse);
        const subscriptionIds = relatedSubscriptions.map(snapshot => snapshot.subscriptionId);
        const mismatches: string[] = [];

        for (const snapshot of relatedSubscriptions) {
            if (snapshot.error && !this.hasBillingFields(snapshot.billTo)) {
                mismatches.push(
                    `Subscription ${snapshot.subscriptionId}: ${snapshot.error}`
                );
            }
        }

        const billToSnapshots = relatedSubscriptions
            .map(snapshot => snapshot.billTo)
            .filter(
                (billTo): billTo is AuthorizeNetCustomerProfileBillingFields =>
                    this.hasBillingFields(billTo)
            );

        if (billToSnapshots.length === 0) {
            mismatches.push(
                'No subscription billing fields were available to verify against (billTo, firstName, or lastName).'
            );
        } else {
            for (const field of ['company', 'address', 'firstName', 'lastName'] as const) {
                const profileValue = this.normalizeCompareValue(summary[field]);
                for (const snapshot of relatedSubscriptions) {
                    if (!this.hasBillingFields(snapshot.billTo)) {
                        continue;
                    }

                    const subscriptionValue = this.normalizeCompareValue(
                        snapshot.billTo![field]
                    );
                    if (!subscriptionValue) {
                        continue;
                    }

                    if (profileValue !== subscriptionValue) {
                        mismatches.push(
                            `${field}: profile="${summary[field] ?? ''}" vs subscription ${snapshot.subscriptionId}="${snapshot.billTo![field] ?? ''}"`
                        );
                    }
                }
            }
        }

        return {
            ...summary,
            subscriptionIds,
            subscriptionBillTo: billToSnapshots[0],
            verified: mismatches.length === 0,
            verificationMismatches: mismatches.length > 0 ? mismatches : undefined
        };
    }

    private extractSubscriptionBillingFields(
        response: AuthorizeNetSubscriptionDetailsListItem | AuthorizeNetSubscriptionResponse,
        listItem?: AuthorizeNetSubscriptionDetail
    ): AuthorizeNetCustomerProfileBillingFields | undefined {
        const root = this.asRecord(response);
        const subscription =
            this.asRecord(this.readRecordValue(root, 'subscription')) ?? root;
        const billToRecord = this.asRecord(this.readRecordValue(subscription, 'billTo'));
        const profileRecord = this.asRecord(this.readRecordValue(subscription, 'profile'));
        const paymentProfiles =
            this.readRecordValue(profileRecord, 'paymentProfile') ??
            this.readRecordValue(profileRecord, 'paymentProfiles');
        const paymentProfile = Array.isArray(paymentProfiles)
            ? this.asRecord(paymentProfiles[0])
            : this.asRecord(paymentProfiles);
        const profilePaymentBillTo = this.asRecord(
            this.readRecordValue(paymentProfile, 'billTo')
        );
        const listRecord = this.asRecord(listItem);

        const fields: AuthorizeNetCustomerProfileBillingFields = {
            company: this.coalesceString(
                'company',
                billToRecord,
                profilePaymentBillTo
            ),
            address:
                this.formatAddress(billToRecord) ??
                this.formatAddress(profilePaymentBillTo),
            firstName: this.coalesceString(
                'firstName',
                billToRecord,
                profilePaymentBillTo,
                subscription,
                listRecord
            ),
            lastName: this.coalesceString(
                'lastName',
                billToRecord,
                profilePaymentBillTo,
                subscription,
                listRecord
            )
        };

        return this.hasBillingFields(fields) ? fields : undefined;
    }

    private asRecord(value: unknown): Record<string, unknown> | undefined {
        return value && typeof value === 'object'
            ? (value as Record<string, unknown>)
            : undefined;
    }

    private readRecordValue(
        record: Record<string, unknown> | undefined,
        key: string
    ): unknown {
        if (!record) {
            return undefined;
        }

        if (record[key] !== undefined) {
            return record[key];
        }

        const match = Object.keys(record).find(
            property => property.toLowerCase() === key.toLowerCase()
        );
        return match ? record[match] : undefined;
    }

    private readString(
        record: Record<string, unknown> | undefined,
        key: string
    ): string | undefined {
        const value = this.readRecordValue(record, key);
        if (value == null) {
            return undefined;
        }

        const text = String(value).trim();
        return text || undefined;
    }

    private coalesceString(
        key: string,
        ...records: Array<Record<string, unknown> | undefined>
    ): string | undefined {
        for (const record of records) {
            const value = this.readString(record, key);
            if (value) {
                return value;
            }
        }

        return undefined;
    }

    private formatAddress(
        record: Record<string, unknown> | undefined
    ): string | undefined {
        if (!record) {
            return undefined;
        }

        const line = this.readString(record, 'address');
        const city = this.readString(record, 'city');
        const state = this.readString(record, 'state');
        const zip = this.readString(record, 'zip');
        if (!line && !city && !state && !zip) {
            return undefined;
        }

        if (line && !city && !state && !zip) {
            return line;
        }

        return [line, city, state, zip].filter(Boolean).join(', ');
    }

    private mapBillToFields(
        billTo: Record<string, unknown> | undefined
    ): AuthorizeNetCustomerProfileBillingFields | undefined {
        if (!billTo) {
            return undefined;
        }

        return {
            company: this.readString(billTo, 'company'),
            address: this.readString(billTo, 'address'),
            firstName: this.readString(billTo, 'firstName'),
            lastName: this.readString(billTo, 'lastName')
        };
    }

    private normalizeCompareValue(value: string | undefined): string {
        return (value ?? '').trim().toLowerCase();
    }

    private hasBillingFields(
        fields: AuthorizeNetCustomerProfileBillingFields | undefined
    ): boolean {
        return [fields?.company, fields?.address, fields?.firstName, fields?.lastName].some(
            value => !!value?.trim()
        );
    }

    private mapCustomerProfileSummary(
        customerProfileId: string,
        response: AuthorizeNetCustomerProfileResponse
    ): AuthorizeNetCustomerProfileSummary {
        const profile = this.asRecord(response.profile);
        const billTo = this.extractBillToFromProfile(profile);

        return {
            customerProfileId:
                (this.readRecordValue(profile, 'customerProfileId') as
                    | string
                    | number
                    | undefined) ?? customerProfileId,
            company: this.readString(billTo, 'company'),
            address: this.formatAddress(billTo),
            firstName: this.readString(billTo, 'firstName'),
            lastName: this.readString(billTo, 'lastName')
        };
    }

    private extractBillToFromProfile(
        profile: Record<string, unknown> | undefined
    ): Record<string, unknown> | undefined {
        const paymentProfiles =
            this.readRecordValue(profile, 'paymentProfiles') ??
            this.readRecordValue(profile, 'paymentProfile');
        const paymentProfile = Array.isArray(paymentProfiles)
            ? this.asRecord(paymentProfiles[0])
            : this.asRecord(paymentProfiles);
        const paymentBillTo = this.asRecord(this.readRecordValue(paymentProfile, 'billTo'));
        if (paymentBillTo) {
            return paymentBillTo;
        }

        const shipToList =
            this.readRecordValue(profile, 'shipToList') ??
            this.readRecordValue(profile, 'shipTo');
        return Array.isArray(shipToList)
            ? this.asRecord(shipToList[0])
            : this.asRecord(shipToList);
    }

    private getAuthorizeNetErrorMessage(err: {
        status?: number;
        error?: unknown;
        message?: string;
    }): string {
        if (err?.status === 0) {
            return (
                'Unable to reach the API (HTTP 0). Rebuild and restart efaact.Central.Service, then check the ' +
                'browser Network tab for CORS or connection errors.'
            );
        }
        const body = err?.error as {
            Message?: string;
            messages?: { message?: Array<{ text?: string }> | { text?: string } };
        };
        if (typeof body === 'string') {
            return body;
        }
        if (body?.Message) {
            return body.Message;
        }
        const msg = body?.messages?.message;
        if (Array.isArray(msg) && msg[0]?.text) {
            return msg[0].text;
        }
        if (msg && !Array.isArray(msg) && (msg as { text?: string }).text) {
            return (msg as { text?: string }).text;
        }
        if (err?.status === 404) {
            return (
                'Authorize.Net API not found (404). Rebuild efaact.Central.Service and restart IIS Express / Visual Studio.'
            );
        }
        return err?.message ?? 'Request failed. Ensure you are signed in.';
    }
}
